import React, { useState, useEffect } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { ChevronLeft, ChevronRight, Calendar, Check, X, Clock, Trash2 } from 'lucide-react';
import Dropdown from '../../components/Dropdown';
import DateFilter from '../../components/DateFilter';
import { isDateInRange, formatAppDate, formatAppDateOnly } from '../../utils/dateUtils';
import AddLead from '../leads/AddLead';
import LeadProfile from '../leads/components/LeadProfile';
import TakeActionModal from '../leads/TakeActionModal';
import { useAuth } from '../../context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const Followups = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFollowups, setTotalFollowups] = useState(0);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [activeModal, setActiveModal] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionLead, setActionLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('Client Requested');
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [closeNotes, setCloseNotes] = useState('');

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [viewingLead, setViewingLead] = useState(null);
  const [isViewMode, setIsViewMode] = useState(true);
  const [deleteLeadId, setDeleteLeadId] = useState(null);

  const confirmDelete = async () => {
    try {
      const { default: api } = await import('../../utils/api');
      await api.delete(`/leads/${deleteLeadId}`);
      setActiveModal(null);
      setShowLeadModal(false);
      setDeleteLeadId(null);
      fetchFollowups();
    } catch (err) {
      console.error(err);
      alert('Failed to delete lead');
    }
  };

  const handleViewLead = async (f) => {
    try {
      const { default: api } = await import('../../utils/api');
      const res = await api.get(`/leads/${f.leadId}`);
      
      const realLead = res.data;
      
      setViewingLead({
        id: realLead.id,
        name: realLead.companyName,
        contact: realLead.contactPerson,
        phone: realLead.phone,
        email: realLead.email,
        address: realLead.address,
        source: realLead.leadSource?.name || 'Unknown',
        assignee: realLead.user?.name || 'Unassigned',
        machine: realLead.machine?.name || 'N/A',
        type: realLead.leadType?.name ? realLead.leadType.name : (realLead.otherInterest ? `Other (${realLead.otherInterest})` : 'Warm'),
        status: realLead.status?.name || 'Pending',
        createdAt: realLead.createdAt,
        notes: realLead.notes,
        followups: realLead.followups || []
      });
      setIsViewMode(true);
      setShowLeadModal(true);
    } catch (err) {
      console.error("Failed to fetch full lead details", err);
      // Fallback if API fails
      setViewingLead({
        id: f.leadId,
        name: f.leadName,
        contact: f.contact,
        phone: f.phone,
        email: f.email,
        source: f.source,
        assignee: f.assignedTo,
        machine: 'N/A',
        type: 'Warm',
        status: f.status,
        createdAt: f.createdAt,
        notes: f.rawLead?.notes,
        followups: f.rawLead?.followups || []
      });
      setShowLeadModal(true);
    }
  };

  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersList, setUsersList] = useState([]);
  const [sourcesList, setSourcesList] = useState([]);
  const [statusesList, setStatusesList] = useState([]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const { default: api } = await import('../../utils/api');
        const [statusesRes, usersRes, sourcesRes] = await Promise.all([
          api.get('/masters/status').catch(() => ({ data: [] })),
          api.get('/auth/users').catch(() => ({ data: [] })),
          api.get('/masters/lead-sources').catch(() => ({ data: [] }))
        ]);
        setStatusesList(statusesRes.data.filter(s => s.status === 'Active'));
        setUsersList(usersRes.data.filter(u => u.status === 'Active'));
        setSourcesList(sourcesRes.data.filter(s => s.status === 'Active'));
      } catch (err) {
        console.error('Failed to fetch filter data', err);
      }
    };
    fetchFilterData();
  }, []);

  const fetchFollowups = async () => {
    try {
      setLoading(true);
      const { default: api } = await import('../../utils/api');
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      if (statusFilter) queryParams.append('statusName', statusFilter);
      if (sourceFilter) queryParams.append('sourceName', sourceFilter);
      if (userFilter) queryParams.append('userName', userFilter);
      if (dateFilter) {
        queryParams.append('dateFilter', dateFilter);
        if (dateFilter === 'custom' && customStartDate && customEndDate) {
          queryParams.append('startDate', customStartDate.toISOString());
          queryParams.append('endDate', customEndDate.toISOString());
        }
      }
      
      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);
      
      const res = await api.get(`/followups?${queryParams.toString()}`);
      let dataToFormat = res.data.data || res.data;
      console.log("Followups data fetched:", dataToFormat);
      
      const formattedFollowups = dataToFormat.map(f => ({
        id: f.id,
        leadId: f.leadId,
        leadName: f.lead?.companyName || 'Unknown Lead',
        contact: f.lead?.contactPerson || '',
        phone: f.lead?.phone || '',
        email: f.lead?.email || '',
        source: f.lead?.leadSource?.name || 'Unknown',
        assignedTo: f.lead?.user?.name || 'Unassigned',
        scheduledAt: formatAppDate(f.date),
        rawDate: f.date,
        purpose: f.notes,
        type: f.type,
        status: f.lead?.status?.name || 'Pending',
        createdAt: f.createdAt,
        rawLead: f.lead
      }));
      setFollowups(formattedFollowups);
      
      if (res.data.data) {
        setTotalPages(res.data.totalPages || 1);
        setTotalFollowups(res.data.total || 0);
      } else {
        setTotalPages(1);
        setTotalFollowups(res.data.length);
      }
    } catch (err) {
      console.error('Failed to fetch followups', err);
    } finally {
      setLoading(false);
    }
  };

  const handleActionSubmit = async () => {
    if (!selectedLead) return;
    try {
      const { default: api } = await import('../../utils/api');

      if (activeModal === 'reschedule') {
        if (!rescheduleDate || !rescheduleTime) {
          import('react-hot-toast').then(({ toast }) => toast.error("Please select a new date and time"));
          return;
        }
        
        let combinedDateTime;
        try {
          const y = rescheduleDate.getFullYear();
          const m = String(rescheduleDate.getMonth() + 1).padStart(2, '0');
          const d = String(rescheduleDate.getDate()).padStart(2, '0');
          const dateStr = `${y}-${m}-${d}`;
          
          // Try standard parsing
          let timeVal = rescheduleTime;
          // Clean up AM/PM if present
          if (timeVal.toLowerCase().includes('pm')) {
            let parts = timeVal.toLowerCase().replace('pm','').trim().split(':');
            let h = parseInt(parts[0], 10);
            if (h !== 12) h += 12;
            timeVal = `${h.toString().padStart(2, '0')}:${parts[1] || '00'}`;
          } else if (timeVal.toLowerCase().includes('am')) {
            let parts = timeVal.toLowerCase().replace('am','').trim().split(':');
            let h = parseInt(parts[0], 10);
            if (h === 12) h = 0;
            timeVal = `${h.toString().padStart(2, '0')}:${parts[1] || '00'}`;
          }
          
          combinedDateTime = new Date(`${dateStr}T${timeVal}:00`);
          if (isNaN(combinedDateTime.getTime())) throw new Error("Invalid time");
        } catch (e) {
          // Fallback if the time parsing fails
          combinedDateTime = rescheduleDate;
        }

        const logNotes = `[Rescheduled] Reason: ${rescheduleReason}. ${rescheduleNotes ? rescheduleNotes : ''}`;

        // 1. Update the existing followup to act as a log entry
        await api.put(`/followups/${selectedLead.id}`, {
          notes: logNotes,
          type: selectedLead.type || 'Call'
        });
        
        // 2. Create a brand new followup for the new date
        await api.post('/followups', {
          leadId: selectedLead.leadId,
          date: combinedDateTime.toISOString(),
          type: selectedLead.type || 'Call',
          notes: ''
        });

        // 3. Update the lead status to 'Reschedule'
        const statusesRes = await api.get('/masters/status');
        const rescheduleStatus = statusesRes.data.find(s => s.name === 'Reschedule');
        if (rescheduleStatus) {
          await api.put(`/leads/${selectedLead.leadId}`, {
            statusId: rescheduleStatus.id
          });
        }
        
        import('react-hot-toast').then(({ toast }) => toast.success("Follow-up rescheduled and Lead status updated!"));
      } else if (activeModal === 'complete') {
        if (!completeNotes) {
          import('react-hot-toast').then(({ toast }) => toast.error("Call summary is required."));
          return;
        }

        let prefix = 'Completed';
        const today = new Date();
        today.setHours(0,0,0,0);
        const scheduledDate = new Date(selectedLead.rawDate || selectedLead.date || new Date());
        scheduledDate.setHours(0,0,0,0);
        if (today < scheduledDate) {
          prefix = 'Cancelled';
        }

        await api.put(`/followups/${selectedLead.id}`, {
          date: new Date().toISOString(),
          notes: `[${prefix}] ${completeNotes}`,
          type: selectedLead.type || 'Call'
        });
        
        const statusesRes = await api.get('/masters/status');
        const completedStatus = statusesRes.data.find(s => s.name === 'Completed');
        if (completedStatus) {
          await api.put(`/leads/${selectedLead.leadId}`, {
            statusId: completedStatus.id
          });
        }
        
        await api.post('/followups', {
          leadId: selectedLead.leadId,
          date: new Date().toISOString(),
          notes: `Lead marked as Completed`,
          type: 'System'
        });
        
        import('react-hot-toast').then(({ toast }) => toast.success("Follow-up processed and Lead marked as Completed!"));
      } else if (activeModal === 'close') {
        if (!closeReason) {
          import('react-hot-toast').then(({ toast }) => toast.error("Closure reason is required."));
          return;
        }
        // Fetch statuses to find the ID of 'Closed'
        const statusesRes = await api.get('/masters/status');
        const closedStatus = statusesRes.data.find(s => s.name === 'Closed');
        if (closedStatus) {
          await api.put(`/leads/${selectedLead.leadId}`, {
            statusId: closedStatus.id
          });
        }
        
        // Log the closure as a followup event
        await api.post('/followups', {
          leadId: selectedLead.leadId,
          date: new Date().toISOString(),
          notes: `[Closed] Reason: ${closeReason}. ${closeNotes}`,
          type: 'Note'
        });
        
        // Delete the pending followup
        await api.delete(`/followups/${selectedLead.id}`);

        import('react-hot-toast').then(({ toast }) => toast.success("Lead closed!"));
      }
      
      setActiveModal(null);
      setCompleteNotes('');
      setCloseReason('');
      setCloseNotes('');
      setRescheduleNotes('');
      fetchFollowups();
    } catch (err) {
      console.error("Action failed", err);
      import('react-hot-toast').then(({ toast }) => toast.error("Action failed to save."));
    }
  };

  useEffect(() => {
    fetchFollowups();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, statusFilter, sourceFilter, userFilter, dateFilter, customStartDate, customEndDate]);

  // Filter Logic
  
  const paginatedFollowups = followups;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Follow-ups & Callbacks</h2>
          <p className="text-muted mt-2">Manage your scheduled calls and follow-ups.</p>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex justify-between items-center gap-4 flex-wrap">
             <input 
               type="text" 
               placeholder="Search by name, contact or phone..." 
               className="input-field" 
               style={{ flex: '1', minWidth: '250px', maxWidth: '320px' }}
               value={searchTerm}
               onChange={(e) => {
                 setSearchTerm(e.target.value);
                 setCurrentPage(1);
               }} 
             />
             <div className="flex gap-4 items-center flex-wrap">
               <div className="flex items-center gap-2">
                 <DateFilter 
                   value={dateFilter}
                   onChange={(val) => {
                     setDateFilter(val);
                     setCurrentPage(1);
                   }}
                 />
               </div>
                <Dropdown 
                  style={{ minWidth: '150px' }}
                  value={statusFilter}
                  placeholder="All Statuses"
                  onChange={(val) => {
                    setStatusFilter(val);
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: '', label: 'All Statuses' },
                    ...statusesList.map(s => ({ value: s.name, label: s.name }))
                  ]}
                />
                <Dropdown 
                  style={{ minWidth: '150px' }}
                  value={sourceFilter}
                  placeholder="All Sources"
                  onChange={(val) => {
                    setSourceFilter(val);
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: '', label: 'All Sources' },
                    ...sourcesList.map(s => ({ value: s.name, label: s.name }))
                  ]}
                />
                {user?.role !== 'Telecaller' && (
                  <Dropdown 
                    style={{ minWidth: '150px' }}
                    value={userFilter}
                    placeholder="All Users"
                    onChange={(val) => {
                      setUserFilter(val);
                      setCurrentPage(1);
                    }}
                    options={[
                      { value: '', label: 'All Users' },
                      ...usersList.map(u => ({ value: u.name, label: u.name })),
                      { value: 'Unassigned', label: 'Unassigned' },
                    ]}
                  />
                )}
                <button className="btn" style={{ border: '1.5px solid var(--color-border)', backgroundColor: 'white' }}>Export</button>
             </div>
          </div>
          
          {/* Custom Date Range Picker */}

          {dateFilter === 'custom' && (
            <div className="flex justify-end gap-4 fade-in">
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">From:</span>
                 <input 
                   type="date" 
                   className="input-field" 
                   value={customStartDate}
                   onChange={e => {
                     setCustomStartDate(e.target.value);
                     setCurrentPage(1);
                   }}
                 />
               </div>
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">To:</span>
                 <input 
                   type="date" 
                   className="input-field" 
                   value={customEndDate}
                   onChange={e => {
                     setCustomEndDate(e.target.value);
                     setCurrentPage(1);
                   }}
                 />
               </div>
            </div>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                <th style={{ padding: '1rem', width: '5%' }}>Sr. No.</th>
                <th style={{ padding: '1rem', width: '5%' }}>Lead No.</th>
                <th style={{ padding: '1rem', width: '22%' }}>Lead Details</th>
                <th style={{ padding: '1rem', width: '15%' }}>Scheduled For</th>
                <th style={{ padding: '1rem', width: '20%' }}>Purpose</th>
                <th style={{ padding: '1rem', width: '15%' }}>Assigned To</th>
                <th style={{ padding: '1rem', width: '13%' }}>Status</th>
                <th style={{ padding: '1rem', width: '10%', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedFollowups.length > 0 ? (
                paginatedFollowups.map((f, index) => (
                  <tr key={f.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {user?.permissions?.includes('leads:read') || user?.permissions?.includes('leads:read_all') ? (
                        <button 
                          onClick={() => handleViewLead(f)}
                          style={{ color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontSize: 'inherit' }}
                          title="View Lead Details"
                        >
                          #{f.id}
                        </button>
                      ) : (
                        <span>#{f.id}</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div className="font-semibold text-main">{f.leadName}</div>
                      <div className="text-xs mt-1">
                        {f.contact} {f.phone && <span className="text-muted">|</span>} {f.phone && <a href={`tel:${f.phone}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{f.phone}</a>}
                      </div>
                      <div className="text-xs mt-1">
                        {f.email && <a href={`mailto:${f.email}`} style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>{f.email}</a>} {f.email && <span className="text-muted">•</span>} <span className="font-semibold">{f.source}</span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem', fontWeight: '500', color: f.scheduledAt.includes('Yesterday') ? 'var(--color-danger)' : 'inherit' }}>
                      {f.scheduledAt}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {(() => {
                        if (!f.purpose) return <span className="text-muted italic">No notes</span>;
                        const match = f.purpose.match(/^\[(?:Edited|Rescheduled)\] Reason:\s(.*?)\.\s?(.*)$/is);
                        if (match) {
                          return (
                            <div className="flex flex-col gap-1.5 items-start">
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '600',
                                backgroundColor: 'var(--color-primary-light)',
                                color: 'var(--color-primary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.025em'
                              }}>
                                {match[1]}
                              </span>
                              {match[2] && <div className="text-sm text-muted truncate" style={{ maxWidth: '300px' }} title={match[2]}>{match[2]}</div>}
                            </div>
                          );
                        }
                        return <div className="text-sm truncate max-w-xs" title={f.purpose}>{f.purpose}</div>;
                      })()}
                    </td>
                    <td style={{ padding: '1rem', fontWeight: '500' }}>
                      <div className="flex items-center gap-2">
                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold' }}>
                          {f.assignedTo.split(' ').map(n => n[0]).join('')}
                        </div>
                        {f.assignedTo}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.6rem', 
                        borderRadius: '9999px', 
                        backgroundColor: (f.status?.trim() === 'Lead Won' || f.status?.trim() === 'Completed' || f.status?.trim() === 'Won') ? '#dcfce7' : 
                                         (f.status?.trim() === 'Lead Lost' || f.status?.trim() === 'Closed' || f.status?.trim() === 'Lost') ? '#fee2e2' :
                                         (f.status?.trim() === 'Pending' || f.status?.trim() === 'Follow-up') ? '#fef9c3' : '#f1f5f9',
                        color: (f.status?.trim() === 'Lead Won' || f.status?.trim() === 'Completed' || f.status?.trim() === 'Won') ? '#166534' : 
                               (f.status?.trim() === 'Lead Lost' || f.status?.trim() === 'Closed' || f.status?.trim() === 'Lost') ? '#991b1b' :
                               (f.status?.trim() === 'Pending' || f.status?.trim() === 'Follow-up') ? '#854d0e' : '#475569',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {f.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {f.status !== 'Completed' ? (
                        user?.permissions?.includes('leads:write') ? (
                          <button 
                            className="btn btn-primary text-sm" 
                            style={{ padding: '0.4rem 0.8rem', borderRadius: 'var(--border-radius-sm)' }}
                            onClick={() => { setActionLead(f.rawLead); setShowActionModal(true); }}
                          >
                            Take Action
                          </button>
                        ) : (
                          <span className="text-muted text-sm" title="No permission to take action">Take Action</span>
                        )
                      ) : (
                        <span className="text-muted text-sm">—</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    No follow-ups found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalFollowups > 0 && (
          <div className="flex justify-between items-center mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-4 text-sm text-muted">
              <div>
                Showing <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{Math.min(currentPage * itemsPerPage, totalFollowups)}</span> of <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{totalFollowups}</span> entries
              </div>
              <div className="flex items-center gap-2">
                <span>Show:</span>
                <Dropdown 
                  style={{ width: '80px', height: '32px' }}
                  value={itemsPerPage}
                  onChange={(val) => {
                    setItemsPerPage(Number(val));
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' }
                  ]}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                className="btn" 
                style={{ padding: '0.5rem', border: '1px solid var(--color-border)', backgroundColor: currentPage === 1 ? '#f8fafc' : 'white', opacity: currentPage === 1 ? 0.5 : 1 }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </button>
              
              <div className="flex items-center gap-1 mx-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button 
                    key={i} 
                    className="btn"
                    style={{ 
                      padding: '0.25rem 0.75rem', 
                      backgroundColor: currentPage === i + 1 ? 'var(--color-primary)' : 'transparent',
                      color: currentPage === i + 1 ? 'white' : 'var(--color-text-main)',
                      fontWeight: currentPage === i + 1 ? '600' : '400'
                    }}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button 
                className="btn" 
                style={{ padding: '0.5rem', border: '1px solid var(--color-border)', backgroundColor: currentPage === totalPages ? '#f8fafc' : 'white', opacity: currentPage === totalPages ? 0.5 : 1 }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Lead Modal */}
      {activeModal === 'view_lead' && selectedLead && (
        <div className="modal-backdrop fade-in" style={{ zIndex: 100 }}>
          <div className="modal-content" style={{ width: '90%', maxWidth: '1200px', padding: 0, overflow: 'hidden' }}>
            <AddLead 
              initialData={selectedLead.rawLead} 
              isViewMode={true}
              onClose={() => { setActiveModal(null); fetchFollowups(); }} 
              onAction={(lead) => {
                // Set the action lead to the one passed or fallback to selectedLead.rawLead
                setActionLead(lead || selectedLead?.rawLead);
                setShowActionModal(true);
              }}
              // We do not provide onEdit/onDelete directly from Followups right now, 
              // as those functions are not natively implemented in Followups.jsx.
              // However, since the user is explicitly requesting full parity across 
              // the app, we should probably add them! Let's implement full parity:
            />
          </div>
        </div>
      )}

      {/* Action Modals */}
      {activeModal && activeModal !== 'view_lead' && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.2s' }}>
          <div className="card" style={{ width: '100%', maxWidth: '500px', padding: '2.5rem', margin: '1rem', backgroundColor: 'white', borderRadius: '24px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                {activeModal !== 'select_action' && (
                  <button onClick={() => setActiveModal('select_action')} className="text-muted hover:text-main" title="Go Back">
                    <ChevronLeft size={24} />
                  </button>
                )}
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                  {activeModal === 'select_action' && `Select Action`}
                  {activeModal === 'complete' && `Complete Follow-up`}
                  {activeModal === 'reschedule' && `Reschedule Follow-up`}
                  {activeModal === 'close' && `Close Lead`}
                </h3>
              </div>
              <button onClick={() => setActiveModal(null)} style={{ padding: '8px', borderRadius: '50%', backgroundColor: '#f1f5f9', color: 'var(--color-text-muted)' }}><X size={20} /></button>
            </div>
            
            {selectedLead && (
              <div className="mb-6" style={{ padding: '1.25rem', backgroundColor: '#f8fafc', border: '1px solid var(--color-border)', borderRadius: '12px' }}>
                <div style={{ fontWeight: '700', color: 'var(--color-text-main)', fontSize: '1.1rem' }}>{selectedLead.leadName}</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                  {selectedLead.contact} {selectedLead.phone && `• ${selectedLead.phone}`}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-4">
              {activeModal === 'select_action' && (
                <div className="flex flex-col" style={{ gap: '1rem' }}>
                  <button className="flex items-center" style={{ padding: '1.25rem', border: '1px solid var(--color-border)', borderRadius: '16px', backgroundColor: 'white', cursor: 'pointer', fontWeight: '600', color: 'var(--color-text-main)', transition: 'all 0.2s', fontSize: '1rem' }} onClick={() => setActiveModal('complete')} onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-success)'; e.currentTarget.style.backgroundColor = '#f0fdf4'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem' }}>
                      <Check size={20} style={{ color: 'var(--color-success)' }} />
                    </div>
                    Mark as Completed
                  </button>
                  <button className="flex items-center" style={{ padding: '1.25rem', border: '1px solid var(--color-border)', borderRadius: '16px', backgroundColor: 'white', cursor: 'pointer', fontWeight: '600', color: 'var(--color-text-main)', transition: 'all 0.2s', fontSize: '1rem' }} onClick={() => setActiveModal('reschedule')} onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.backgroundColor = '#eef2ff'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(99, 102, 241, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem' }}>
                      <Clock size={20} style={{ color: 'var(--color-primary)' }} />
                    </div>
                    Reschedule Follow-up
                  </button>
                  <button className="flex items-center" style={{ padding: '1.25rem', border: '1px solid var(--color-border)', borderRadius: '16px', backgroundColor: 'white', cursor: 'pointer', fontWeight: '600', color: 'var(--color-text-main)', transition: 'all 0.2s', fontSize: '1rem' }} onClick={() => setActiveModal('close')} onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-danger)'; e.currentTarget.style.backgroundColor = '#fef2f2'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(239, 68, 68, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem' }}>
                      <X size={20} style={{ color: 'var(--color-danger)' }} />
                    </div>
                    Close Lead
                  </button>
                </div>
              )}

              {activeModal === 'complete' && (
                <>
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-muted">Call Summary / Notes *</label>
                    <textarea 
                      className="input-field" 
                      rows="4" 
                      placeholder="Briefly summarize the conversation..." 
                      required 
                      style={{ borderRadius: '12px' }}
                      value={completeNotes}
                      onChange={e => setCompleteNotes(e.target.value)}
                    ></textarea>
                  </div>
                </>
              )}
              
              {activeModal === 'reschedule' && (
                <div className="mb-6">
                  <label className="text-sm font-semibold mb-2 block text-muted">New Date & Time *</label>
                  <DatePicker
                    selected={rescheduleDate}
                    onChange={(date) => {
                      setRescheduleDate(date);
                      if (date) {
                        const hours = String(date.getHours()).padStart(2, '0');
                        const mins = String(date.getMinutes()).padStart(2, '0');
                        setRescheduleTime(`${hours}:${mins}`);
                      } else {
                        setRescheduleTime('');
                      }
                    }}
                    className="input-field w-full"
                    dateFormat="dd-MMM-yyyy hh:mm aa"
                    placeholderText="Select Date & Time"
                    minDate={new Date()}
                    showTimeSelect
                    timeFormat="hh:mm aa"
                    timeIntervals={15}
                    required
                    style={{ borderRadius: '12px' }}
                    minTime={
                      rescheduleDate && new Date(rescheduleDate).setHours(0,0,0,0) === new Date().setHours(0,0,0,0)
                        ? new Date()
                        : new Date(new Date().setHours(0, 0, 0, 0))
                    }
                    maxTime={new Date(new Date().setHours(23, 59, 59, 999))}
                  />
                </div>
              )}
              
              {activeModal === 'reschedule' && (
                <>
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-muted">Reason for Rescheduling</label>
                    <Dropdown 
                      style={{ width: '100%', borderRadius: '12px' }}
                      value={rescheduleReason}
                      onChange={(val) => setRescheduleReason(val)}
                      options={[
                        { value: 'Client Requested', label: 'Client Requested' },
                        { value: 'Busy / No Answer', label: 'Busy / No Answer' },
                        { value: 'Follow-up needed later', label: 'Follow-up needed later' }
                      ]}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-muted">Notes (Optional)</label>
                    <textarea className="input-field" rows="3" placeholder="Add any details about this follow-up..." style={{ borderRadius: '12px' }} value={rescheduleNotes} onChange={e => setRescheduleNotes(e.target.value)}></textarea>
                  </div>
                </>
              )}

              {activeModal === 'close' && (
                <>
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-muted">Closure Reason *</label>
                    <Dropdown 
                      placeholder="Select Reason..."
                      style={{ width: '100%', borderRadius: '12px' }}
                      value={closeReason}
                      onChange={(val) => setCloseReason(val)}
                      options={[
                        { value: 'Not Interested', label: 'Not Interested' },
                        { value: 'Out of Budget', label: 'Out of Budget' },
                        { value: 'Already Purchased Elsewhere', label: 'Already Purchased Elsewhere' },
                        { value: 'Invalid Contact Info', label: 'Invalid Contact Info' }
                      ]}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-muted">Final Notes</label>
                    <textarea 
                      className="input-field" 
                      rows="3" 
                      placeholder="Any final thoughts before closing this lead..." 
                      style={{ borderRadius: '12px' }}
                      value={closeNotes}
                      onChange={e => setCloseNotes(e.target.value)}
                    ></textarea>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-8 pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <button className="btn" onClick={() => setActiveModal(null)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white', borderRadius: '10px' }}>Cancel</button>
              {activeModal !== 'select_action' && (
                <button className="btn btn-primary" onClick={handleActionSubmit} style={{ borderRadius: '10px', padding: '0.75rem 1.5rem' }}>
                  {activeModal === 'complete' && 'Mark Completed'}
                  {activeModal === 'reschedule' && 'Confirm Reschedule'}
                  {activeModal === 'close' && 'Close Lead'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showLeadModal && viewingLead && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 50,
          padding: '2rem'
        }}>
          <div className="hide-scrollbar" style={{
            backgroundColor: 'var(--color-bg-card)',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh', overflow: 'hidden',
            borderRadius: '1.5rem',
            boxShadow: 'var(--shadow-lg)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <AddLead 
              onClose={() => setShowLeadModal(false)}
              onSave={() => { setShowLeadModal(false); fetchFollowups(); }}
              initialData={viewingLead}
              isViewMode={isViewMode}
              onEdit={isViewMode ? () => setIsViewMode(false) : undefined}
              onDelete={(id) => setDeleteLeadId(id)}
              onAction={(lead) => {
                setActionLead(lead);
                setShowActionModal(true);
              }}
            />
          </div>
        </div>
      )}

      {deleteLeadId && (
        <div className="modal-backdrop">
          <div className="modal-content fade-in" style={{ maxWidth: '400px', padding: '2rem', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} className="text-danger" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete Lead</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this lead? This action cannot be undone and all associated follow-ups will be permanently removed.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn btn-secondary flex-1" onClick={() => setDeleteLeadId(null)}>
                Cancel
              </button>
              <button className="btn btn-danger flex-1" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Followups;
